import { debounceTime } from "rxjs";
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { MenuItem } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { TranslateService } from "@ngx-translate/core";
import * as FlatModels from "@medsurf/flat-models";
import * as FlatActions from "@medsurf/flat-actions";
import * as FlatDesigns from "@medsurf/flat-designs";
import * as FlatFacades from "@medsurf/flat-facades";
import * as FlatServices from "@medsurf/flat-services";
import { ModalControlComponent } from '@medsurf/flat-components';
import { PageTitleDialogComponent } from "../../../common/page-title-dialog/page-title-dialog.component";
import { PageLinkDialogComponent } from '../../../common/page-link-dialog/page-link-dialog.component';

@Component({
    selector: 'medsurf-chapter-main-slides',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './slides.component.html',
    styleUrls: ['./slides.component.scss'],
    standalone: false
})
export class SlidesComponent {
  @ViewChild('slides_data_view', { static: false }) slidesDataView: FlatDesigns.SortableDataViewContentDesign;

  #handled = false;
  private typedNodeExpanded: Record<string, boolean> = {};

  slideAddMenuItems: MenuItem[] = [
    {
      label: this.translate.instant('global.slide_add'),
      disabled: true,
      styleClass: 'menu_grouping_label',
    },
    {
      label: this.translate.instant('global.slide'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_SLIDE,
      command: () => this.addSlide(),
    },
    {
      label: this.translate.instant('global.training'),
      icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.PAGE_TRAINING,
      command: () => this.addSlide(true),
    },
  ]

  getSlidesCloneMenuItems = () => {
    const selectNodeSet = this.bulkEditAuthorFacade.snapshot_selectNodeSet();
    const parentNode = this.nodeSharedFacade.snapshot_currentSelectedBlockNode();
    const disabled = selectNodeSet.size === 0 || !parentNode;
    return [
      {
        label: this.translate.instant('global.node_duplicate'),
        disabled: true,
        styleClass: 'menu_grouping_label',
      },
      {
        label: this.translate.instant('global.node_duplicate_without_annotations'),
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DUPLICATE,
        command: () => parentNode && this.duplicateNodes(parentNode, false, false),
      },
      {
        label: this.translate.instant('global.node_duplicate_with_annotations'),
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DUPLICATE_WITH_ANNOTATIONS,
        command: () => parentNode && this.duplicateNodes(parentNode, false, true),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.node_duplicate_as_training_without_annotations'),
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DUPLICATE_AS_TRAINING,
        command: () => parentNode && this.duplicateNodes(parentNode, true, false),
      },
      {
        label: this.translate.instant('global.node_duplicate_as_training_with_annotations'),
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DUPLICATE_AS_TRAINING_WITH_ANNOTATIONS,
        command: () => parentNode && this.duplicateNodes(parentNode, true, true),
      }
    ] satisfies MenuItem[];
  }
  getSlidesOthersMenuItems = () => {
    const selectNodeSet = this.bulkEditAuthorFacade.snapshot_selectNodeSet();
    const selectPageSet = this.bulkEditAuthorFacade.snapshot_selectPageSet();
    const nodesVisible = this.bulkEditAuthorFacade.snapshot_nodesVisible();
    const disabled = selectNodeSet.size === 0;
    return [
      {
        label: this.translate.instant(nodesVisible ? 'global.visibility_edit_visible' : 'global.visibility_edit_hidden'),
        disabled: disabled,
        icon: (!nodesVisible ? (FlatModels.ButtonDesignModels.ButtonDefaultIcons.EYE_SLASH + FlatModels.ButtonDesignModels.ButtonDefaultIconsStyle.DANGER) : FlatModels.ButtonDesignModels.ButtonDefaultIcons.EYE),
        command: () => this.toggleVisibilities(nodesVisible)
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('global.pages_link'),
        disabled: selectPageSet.size === 0,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.ATTACH,
        command: () => this.linkPages()
      },
      {
        label: this.translate.instant('global.nodes_detach'),
        disabled: disabled,
        icon: FlatModels.ButtonDesignModels.ButtonDefaultIcons.DETACH,
        command: () => this.detachNodes()
      }
    ] satisfies MenuItem[];
  }
  readonly FlatModels = FlatModels;

  constructor(
    public navigationControlFacade: FlatFacades.NavigationControlFacade,
    public alertControlFacade: FlatFacades.AlertControlFacade,
    public appSharedFacade: FlatFacades.AppSharedFacade,
    public nodeSharedFacade: FlatFacades.NodeSharedFacade,
    public bulkEditAuthorFacade: FlatFacades.BulkEditAuthorFacade,
    public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
    public bookmarkAuthorFacade: FlatFacades.BookmarkAuthorFacade,
    public historySharedFacade: FlatFacades.HistorySharedFacade,
    public mediaControlService: FlatServices.MediaControlService,
    public dialogService: DialogService,
    public translate: TranslateService) {
    this.slideAuthorFacade.typedNodeExpanded$.pipe(takeUntilDestroyed()).subscribe((typedNodeExpanded) => {
      this.typedNodeExpanded = typedNodeExpanded;
    });

    // Scroll Into View The Given Node In The Slide Data View
    this.slideAuthorFacade.ofActionDispatchedScrollNodeIntoView.pipe(takeUntilDestroyed(), debounceTime(200)).subscribe((data: { action: FlatActions.SlideAuthorActions.ScrollNodeTreeElementIntoView }) => {
      const element = document.getElementById('s-' + data.action.id);
      if (element && this.slidesDataView) {
        this.slidesDataView.scrollTo(element.offsetTop);
      }
    });
  }

  onToggleBookmark(node: FlatModels.NodeEntityModels.NodeEntityType,
    page: FlatModels.PageEntityModels.PageEntityType) {
    this.#handled = true;
    this.bookmarkAuthorFacade.requestToggleNode(node, page).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  onToggleBulkNode(id: string,
    parentId: string) {
    this.#handled = true;
    this.bulkEditAuthorFacade.requestToggleNode(id, parentId).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  onToggleBlockChildrenBulkNodes(node: FlatModels.NodeEntityModels.BlockNode,
    override: boolean) {
    if (override) {
      this.bulkEditAuthorFacade.requestSetNodes(node.children).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    } else {
      this.bulkEditAuthorFacade.requestRemoveNodes(node.children).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    }
  }

  trackBy(index: number,
    item: FlatModels.NodeEntityModels.NodeEntityType) {
    return item.id + item.position || index;
  }

  changeLayout(event: {layout: 'list' | 'grid'}) {
    this.slideAuthorFacade.requestSetLayout(event.layout).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  //<editor-fold desc="Drag And Drop">

  onNodeMoved(parent: FlatModels.NodeEntityModels.NodeEntityType | null,
    children: FlatModels.NodeEntityModels.NodeEntityType[]) {
    this.nodeSharedFacade.requestRearrangeNodes(parent, children).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  //</editor-fold>

  onNodeClick(event: MouseEvent,
    node: FlatModels.NodeEntityModels.NodeEntityType,
    typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }) {
    if (this.#handled) {
      this.#handled = false;
      return;
    }
    document.getSelection()?.removeAllRanges();
    if (event.ctrlKey || event.metaKey) {
      return this.bulkEditAuthorFacade.requestToggleNode(node.id, node.parentId).subscribe({
        error: (result) => this.alertControlFacade.requestAddError(result),
      });
    } else {
      this.goToSlide(typedRouteElements[node.id])
    }
  }

  goToSlide(routeElement: FlatModels.NodeEntityModels.RouteElement) {
    this.navigationControlFacade.requestNavigate([routeElement.path]);
  }

  deselectNodes() {
    this.bulkEditAuthorFacade.requestDeselectNodes().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  private duplicateNodes(parentNode: FlatModels.NodeEntityModels.NodeEntityType,
    asTraining: boolean,
    withAnnotations: boolean) {
    this.bulkEditAuthorFacade.requestDuplicateNodes(parentNode, asTraining, withAnnotations).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
      next: () => {
        this.typedNodeExpanded[parentNode.id] = true;
        this.slideAuthorFacade.requestSetTypedNodeExpanded(this.typedNodeExpanded).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }

  copyNodes() {
    this.bulkEditAuthorFacade.requestCopyNodesFromSelectSet().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  pasteNodes(parentNode: FlatModels.NodeEntityModels.NodeEntityType) {
    this.bulkEditAuthorFacade.requestPasteNodes(parentNode, null, true).subscribe({
      error: (result) => {
        this.alertControlFacade.requestAddError(result)
      },
      next: () => {
        this.typedNodeExpanded[parentNode.id] = true;
        this.slideAuthorFacade.requestSetTypedNodeExpanded(this.typedNodeExpanded).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }

  removeNodes() {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.nodes_remove'),
      data: {
        label: 'global.nodes_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        this.bulkEditAuthorFacade.requestRemoveBulkEditNodes().subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }

  private toggleVisibilities(hidden: boolean) {
    this.bulkEditAuthorFacade.requestToggleVisibilities(hidden).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  private linkPages() {
    const dialogRef = this.dialogService.open(PageLinkDialogComponent, {
      header: this.translate.instant('global.pages_link_title'),
      dismissableMask: true,
      width: '70%',
      height: '90%',
    });
    dialogRef.onClose.subscribe(data => {
      if (data && data.result) {
        // Request Attach Pages
        this.bulkEditAuthorFacade.requestAttachSelectedPagesToNodes(data.nodes).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
          next: () => {
            for (const node of data.nodes) {
              this.typedNodeExpanded[node.id] = true;
              this.slideAuthorFacade.requestSetTypedNodeExpanded(this.typedNodeExpanded).subscribe({
                error: (result) => this.alertControlFacade.requestAddError(result),
              });
            }
            this.alertControlFacade.requestAddAlert({
              key: 'main',
              detail: 'notification.slides_linked',
              severity: FlatModels.AlertControlModels.AlertType.enum.success,
              life: FlatModels.AlertControlModels.AlertTime.DEFAULT
            });
          }
        });
      }
    });
  }

  private detachNodes() {
    this.bulkEditAuthorFacade.requestDetachNodes().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  public addSlide(isTraining = false) {
    const routeElement = structuredClone(this.nodeSharedFacade.snapshot_currentSelectedRouteElement());
    if (!routeElement) return;
    // Show Dialog
    const dialogRef = this.dialogService.open(PageTitleDialogComponent, {
      header: this.translate.instant(isTraining ? 'global.training_add_description' : 'global.slide_add_description'),
      data: {
        inputLabel: this.translate.instant('global.add')
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(data => {
      if (routeElement && data?.result) {
        // Request Add Node
        this.nodeSharedFacade.requestAddNodeAfterCurrent(routeElement, null, 'history.node_add', data.title, isTraining).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }

  addSlideSplitButton(routeElement: FlatModels.NodeEntityModels.RouteElement) {
    const dialogRef = this.dialogService.open(PageTitleDialogComponent, {
      header: this.translate.instant('global.slide_add_description'),
      data: {
        inputLabel: this.translate.instant('global.add')
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(data => {
      if (data && data.result) {
        this.nodeSharedFacade.requestAddNodeAfterCurrent(routeElement, null, 'history.node_add', data.title, false).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
}
